// src/merch/designs.js
//
// Cloud-saved designs (Save project + 3D view links).
//   saveDesign()               → { id, editToken, editUrl, viewUrl }
//   saveDesign({ frozen })     → read-only snapshot (orders)
//   loadDesignData(id)         → { meta, data }
//   applyDesign(data)          → put a loaded design into the editor

import { snapshot } from "valtio";
import { merch, layersForSide } from "./store";
import { commitPaintAsLayer } from "./paint";
import { getProduct, getSize, getSides } from "./products";
import { supabase, callFn } from "./supabase";
import { resetHistory } from "./history";
import { designPreview } from "./preview";

/** Base URL of the app (merchmod.ca/… or tattune.ca/merch/…). */
export function appBase() {
  const prefix = /^\/merch(?=\/|$)/.test(window.location.pathname) ? "/merch" : "";
  return `${window.location.origin}${prefix}`;
}
export const editUrlFor = (id, token) => `${appBase()}/?d=${id}&k=${token}`;
export const viewUrlFor = (id) => `${appBase()}/view/${id}`;

/** Everything needed to rebuild the design exactly (all zones). */
export function designJson() {
  if (merch.paint.strokes) commitPaintAsLayer();
  const s = snapshot(merch);
  const product = getProduct(s.productId);
  const size = getSize(product, s.sizeId);
  return {
    version: 2,
    productId: s.productId,
    sizeId: s.sizeId,
    colourId: s.colourId,
    hand: s.hand,
    sides: Object.fromEntries(getSides(product, size, s.hand).map((z) => [z.id, layersForSide(z.id)])),
    savedAt: new Date().toISOString(),
  };
}

export async function saveDesign({ frozen = false, title = null } = {}) {
  const data = designJson();
  const prev = !frozen ? merch.saved : null;
  const res = await callFn("merch-design", {
    action: "save",
    frozen,
    id: prev?.id,
    editToken: prev?.editToken,
    meta: { productId: data.productId, sizeId: data.sizeId, colourId: data.colourId, hand: data.hand, title },
  });
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
  const { error } = await supabase.storage.from("merch-designs").uploadToSignedUrl(res.upload.path, res.upload.token, blob, {
    contentType: "application/json",
    upsert: true,
  });
  if (error) throw new Error("Couldn't save your design — please try again.");
  // small picture for the design email (best effort: never blocks saving)
  if (res.previewUpload) {
    try {
      const png = await designPreview(600);
      if (png) await supabase.storage.from("merch-designs").uploadToSignedUrl(res.previewUpload.path, res.previewUpload.token, png, { contentType: "image/png", upsert: true });
    } catch (e) { console.warn("[designs] preview upload skipped", e); }
  }
  if (!frozen) {
    merch.saved = { id: res.id, editToken: res.editToken };
    merch.rev++; // persist the link locally too
  }
  return {
    id: res.id,
    editToken: res.editToken,
    editUrl: res.editToken ? editUrlFor(res.id, res.editToken) : null,
    viewUrl: viewUrlFor(res.id),
  };
}

/** Email the saved design's links (edit + 3D). Needs a saved design. */
export async function emailDesignLinks({ email, marketing }) {
  const sv = merch.saved;
  if (!sv?.id || !sv?.editToken) throw new Error("Save your design first.");
  return callFn("merch-design", {
    action: "email",
    id: sv.id,
    editToken: sv.editToken,
    email,
    marketing: !!marketing,
    appBaseUrl: window.location.href,
  });
}

export async function loadDesignData(id) {
  const { meta, url } = await callFn("merch-design", { action: "load", id });
  const r = await fetch(url);
  if (!r.ok) throw new Error("Couldn't open that design.");
  return { meta, data: await r.json() };
}

/** Load a design into the editor (replaces the current one). */
export function applyDesign(data, saved = null) {
  const product = getProduct(data.productId);
  const size = getSize(product, data.sizeId);
  const zones = getSides(product, size, data.hand);
  const first = zones.find((z) => (data.sides?.[z.id] || []).length) || zones[0];
  const parked = {};
  for (const z of zones) if (z.id !== first.id) parked[z.id] = data.sides?.[z.id] || [];
  Object.assign(merch, {
    productId: product.id,
    sizeId: size.id,
    colourId: data.colourId || merch.colourId,
    hand: data.hand === "left" ? "left" : "right",
    side: first.id,
    layers: data.sides?.[first.id] || [],
    sideLayers: parked,
    selectedId: null,
    saved,
  });
  merch.sideRev++;
  merch.rev++;
  resetHistory();
}
